import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { examinerLimitFields, examinerScope, validateExaminerLimits } from '../../utils/examinerApi';
import { ExaminerError, ExaminerReadiness, useExaminer } from './ExaminerShared';
import './examiner.css';
import { EXAMINER_AVAILABILITY_CHANGED } from '../../utils/useExaminerVisibility';

const SettingsForm = () => {
  const { request, run, busy, error, forbidden } = useExaminer();
  const [record, setRecord] = useState(null);
  const [values, setValues] = useState({});
  const [saved, setSaved] = useState(false);
  const [enabled, setEnabled] = useState(null);
  const [enabledSaved, setEnabledSaved] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);
  const apply = data => {
    setRecord(data);
    setEnabled(typeof data.enabled === 'boolean' ? data.enabled : null);
    setValues(Object.fromEntries(examinerLimitFields.map(([key]) => [key, data.limits[key]])));
  };
  const load = () => run(async () => { apply(await request('/settings')); setSaved(false); setEnabledSaved(false); setAvailabilityError(null); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = event => {
    event.preventDefault();
    setSaved(false);
    run(async () => {
      const message = validateExaminerLimits(values);
      if (message) throw new Error(message);
      const data = await request('/settings', { method: 'POST', body: Object.fromEntries(examinerLimitFields.map(([key]) => [key, Number(values[key])])) });
      apply(data);
      setSaved(true);
    });
  };
  const saveEnabled = event => {
    event.preventDefault();
    setEnabledSaved(false);
    setAvailabilityError(null);
    run(async () => {
      if (typeof enabled !== 'boolean') throw new Error('Reload examiner settings to read the enabled state before saving.');
      try {
        const data = await request('/settings', { method: 'POST', body: { enabled } });
        if (typeof data.enabled !== 'boolean' || data.enabled !== enabled) throw new Error('The server did not save the requested examiner enabled state. Reload examiner settings.');
        setRecord(data);
        setEnabled(data.enabled);
        setEnabledSaved(true);
        window.dispatchEvent(new Event(EXAMINER_AVAILABILITY_CHANGED));
      } catch (failure) {
        setAvailabilityError(failure);
        throw failure;
      }
    });
  };
  return <section className="examiner" aria-label="AI Mock Examiner settings">
    <h2>AI Mock Examiner</h2>
    <p>Minutes are reserved at examination creation, not billed elapsed minutes. Session duration is a maximum: a new exam uses the smaller remaining user or organization allowance, rounded down to whole minutes (minimum one minute). Failed or cancelled startups release unused reservations; provider processing already attempted remains accounted for. Finished or deleted sessions settle their unused time, but sessions with real audio still count toward the daily session limit. Changes apply to new exams only; existing exam allowances and deadlines remain unchanged. Retention remains 30 days.</p>
    <ExaminerError error={error !== availabilityError ? error : null} />
    {forbidden.includes('/settings') ? <p>Organization administrator access is required.</p> : <>
      <button disabled={busy} onClick={load}>Reload examiner settings</button>
      {record && <form onSubmit={saveEnabled}>
        <h3>Virtual examiner availability</h3>
        <p>Private, Instrument and Commercial (including Commercial Multi) use server-managed preparation. Generation may incur provider charges. No examiner preference, ACS URL, launch settings or usage-limit override is required to take an oral. Existing matching work is reused; stopped jobs are not automatically retried. Disabling preserves records; already queued/running preparation can finish.</p>
        <ExaminerReadiness readiness={record.readiness} />
        {enabled === null ? <p role="alert">The server did not return the examiner enabled state. Reload settings after updating the API.</p> : <>
          <label className="examiner-check"><input type="checkbox" checked={enabled} disabled={busy} onChange={event => { setEnabled(event.target.checked); setEnabledSaved(false); }} />Enable virtual examiner</label>
          <p>Saved organization setting: {record.enabled ? 'On' : 'Off'}. This control does not change usage limits or checkride launch defaults.</p>
        </>}
        <button type="submit" disabled={busy || enabled === null}>Save examiner availability</button>
        {availabilityError ? <div role="alert">
          <strong>Examiner availability was not saved.</strong>
          <p>The checkbox is your requested value, not confirmation that the examiner is enabled.</p>
          <ExaminerError error={availabilityError} />
          {availabilityError.readiness?.status === 'blocked' && <p>The server refused enablement. A backend operator must resolve the blockers above; changing the checkbox cannot enable missing server services.</p>}
        </div> : !enabledSaved && enabled !== null && enabled !== record.enabled && <p role="status">Unsaved change. Click Save examiner availability to apply it.</p>}
        {enabledSaved && <p role="status">Virtual examiner {record.enabled ? 'enabled' : 'disabled'} for this organization.</p>}
      </form>}
      {record && <form onSubmit={submit}>
        <h3>Optional usage-limit overrides</h3>
        <p>Existing server defaults apply without saving this form.</p>
        <p>{record.settingsId === null ? 'No saved override — organization defaults displayed.' : `Saved override. Updated: ${record.updatedAt || 'Unknown'}; by account ${record.updatedBy ?? 'Unknown'}.`}</p>
        {saved && <p role="status">Examiner settings saved. New examinations will load the updated limits.</p>}
        <p>Daily reset timezone: <strong>UTC</strong>. Zero/unlimited values are not supported.</p>
        <div className="examiner-limits">{examinerLimitFields.map(([key, label, min, max]) => <label key={key}><span>{label}</span>
          <input type="number" required min={min} max={max} step="1" value={values[key] ?? ''} onChange={event => setValues(previous => ({ ...previous, [key]: event.target.value }))} />
        </label>)}</div>
        <button disabled={busy} type="submit">{busy ? 'Saving/loading...' : 'Save examiner limits'}</button>
      </form>}
    </>}
  </section>;
};

const ExaminerSettings = () => {
  const { currentUser, currentOrg } = useApp();
  if (!['admin', 'superuser'].includes(currentUser?.user?.orgRole)) return null;
  return <SettingsForm key={`${examinerScope(currentUser)}:${currentOrg?.id}`} />;
};
export default ExaminerSettings;
